/**
 * Functions to offset and thicken paths
 * @module
 */
import { angleClean } from "./angle";
import { closestPointPolyLine2Edge } from "./closestPoint";
import { ANGLE_EPSILON, DIST_EPSILON, DIST_EPSILON_SQUARED, ZERO_EPSILON } from "./core";
import { line2Normal, line2Vec } from "./line2";
import type { Plane } from "./plane";
import { planeDistanceTo, planeNewXY, planeNormal, planeProjectPolyLine3, polyLine2OnPlane } from "./plane";
import {
  polyLine2Concat,
  polyLine2Copy,
  polyLine2DetectStarPoints,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeNormal,
  polyLine2EdgeStart,
  polyLine2IsClosed,
  polyLine2RasterFill,
  polyLine2RepairEdgeIndex,
  polyLine2ReverseCopy,
  polyLine2Shift,
} from "./polyLine2";
import { polyLine2New, type PolyLine2 } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { DEFAULT_CONFIG } from "./straightSkeleton/skeletonConstants";
import type { EdgeSpeeds, SkeletonConfig } from "./straightSkeleton/skeletonTypes";
import { straightSkeletonOffset } from "./straightSkeleton/straightSkeleton";
import {
  vec2Add,
  vec2AngleBetween,
  vec2DistSquared,
  vec2Dot,
  vec2Len,
  vec2Result,
  vec2Scale,
  vec2SequenceIsTurning,
  type Vec2,
} from "./vec2";

const { abs } = Math;

/**
 * Offset a polyline along the normals of its edges.
 * @param pl Polyline to offset.
 * @param dist Counterclockwise polylines are expanded by a positive dist value, and contracted by a negative dist value. For clockwise polylines the behavior is opposite.
 * @returns
 */
export function offsetPolyLine2(pl: PolyLine2, dist: number): PolyLine2 {
  const closed = polyLine2IsClosed(pl);

  let numStarPts = 0;

  const isStarPt = polyLine2DetectStarPoints(pl);
  for (let i = 0, il = isStarPt.length; i < il; i++) {
    if (isStarPt[i]) {
      numStarPts++;
    }
  }

  const plen = pl.length;
  const outlen = plen + 2 * numStarPts;

  const res = polyLine2New(outlen);

  if (closed) {
    // last point is coincident w/ first point
    [res[0], res[1]] = polyLine2MiterPt(pl[plen - 4], pl[plen - 3], pl[0], pl[1], pl[2], pl[3], dist);

    res[outlen - 2] = res[0];
    res[outlen - 1] = res[1];
  } else {
    [res[0], res[1]] = offsetLine2Vertex(pl[0], pl[1], pl[2], pl[3], dist, true);
    [res[outlen - 2], res[outlen - 1]] = offsetLine2Vertex(
      pl[plen - 4],
      pl[plen - 3],
      pl[plen - 2],
      pl[plen - 1],
      dist,
      false,
    );
  }

  const l = closed ? plen - 4 : plen - 4;
  for (let i = 0, cursor = 0; i < l; i += 2) {
    const x0 = pl[i];
    const y0 = pl[i + 1];

    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    const x2 = pl[i + 4];
    const y2 = pl[i + 5];

    if (!isStarPt[i / 2]) {
      [res[cursor + 2], res[cursor + 3]] = polyLine2MiterPt(x0, y0, x1, y1, x2, y2, dist);
      cursor += 2;
    } else {
      const [normx, normy] = line2Normal(x0, y0, x1, y1);

      const [offsetx0, offsety0] = vec2Add(normx, normy, x1, y1);
      const [offsetx1, offsety1] = vec2Add(-normx, -normy, x1, y1);

      [res[cursor + 2], res[cursor + 3]] = polyLine2MiterPt(x0, y0, x1, y1, offsetx0, offsety0, dist);
      [res[cursor + 4], res[cursor + 5]] = polyLine2MiterPt(offsetx1, offsety1, x1, y1, x2, y2, dist);

      cursor += 4;
    }
  }

  return res;
}

export function offsetPolyLine2Variable(pl: PolyLine2, dists: number[]): PolyLine2 {
  const closed = polyLine2IsClosed(pl);

  let numStarPts = 0;

  const isStarPt = polyLine2DetectStarPoints(pl);
  for (let i = 0, il = isStarPt.length; i < il; i++) {
    if (isStarPt[i]) {
      numStarPts++;
    }
  }

  const plen = pl.length;
  const outlen = plen + 2 * numStarPts;

  const res = polyLine2New(outlen);

  if (closed) {
    // last point is coincident w/ first point
    [res[0], res[1]] = polyLine2VariableMiterPt(
      pl[plen - 4],
      pl[plen - 3],
      pl[0],
      pl[1],
      pl[2],
      pl[3],
      dists[dists.length - 1],
      dists[0],
    );

    res[outlen - 2] = res[0];
    res[outlen - 1] = res[1];
  } else {
    [res[0], res[1]] = offsetLine2Vertex(pl[0], pl[1], pl[2], pl[3], dists[0], true);
    [res[outlen - 2], res[outlen - 1]] = offsetLine2Vertex(
      pl[plen - 4],
      pl[plen - 3],
      pl[plen - 2],
      pl[plen - 1],
      dists[dists.length - 1],
      false,
    );
  }

  const l = closed ? plen - 4 : plen - 4;
  const dl = dists.length;
  for (let i = 0, cursor = 0; i < l; i += 2) {
    const i2 = i / 2;

    const x0 = pl[i];
    const y0 = pl[i + 1];

    const x1 = pl[i + 2];
    const y1 = pl[i + 3];

    const x2 = pl[i + 4];
    const y2 = pl[i + 5];

    if (!isStarPt[i2]) {
      const miter = polyLine2VariableMiterPt(x0, y0, x1, y1, x2, y2, dists[i2], dists[(i2 + 1) % dl]);

      res[cursor + 2] = miter[0];
      res[cursor + 3] = miter[1];

      cursor += 2;
    } else {
      const [normx, normy] =
        vec2DistSquared(x0, y0, x1, y1) > DIST_EPSILON_SQUARED
          ? line2Normal(x0, y0, x1, y1)
          : line2Normal(x1, y1, x2, y2);

      const [offsetx0, offsety0] = vec2Add(normx, normy, x1, y1);
      const [offsetx1, offsety1] = vec2Add(-normx, -normy, x1, y1);

      let miter = polyLine2VariableMiterPt(x0, y0, x1, y1, offsetx0, offsety0, dists[i2], dists[(i2 + 1) % dl]);

      res[cursor + 2] = miter[0];
      res[cursor + 3] = miter[1];

      miter = polyLine2VariableMiterPt(offsetx1, offsety1, x1, y1, x2, y2, dists[i2], dists[(i2 + 1) % dl]);

      res[cursor + 4] = miter[0];
      res[cursor + 5] = miter[1];

      cursor += 4;
    }
  }

  return res;
}

/**
 * Same as offsetPolyLine2Variable, but instead of a smooth transition between edge offset dists,
 * it creates a sharp transition.
 * @TODO This function is not working properly when duplicate points are inputted. See failure test cases in polyLine2.test.ts
 *
 * @param polyLineToOffset
 * @param edgeOffsetDistances Array of distances to offset each edge normal to the edge tangent.
 * @param collinearStartAndEndAddSegment If true, collinear adjacent start and end edges in the original polyline
 *  with dissimilar offsets will add a segment to close the gap.
 * @returns The offset result containing:
 * - `pl` (`PolyLine2`): The offset polyline.
 * - `edgeMapping` (`(number | undefined)[]`): An array of edge indices mapping to the original polyline edges.
 * Undefined values indicate new edges without a mapping.
 */
export function offsetPolyLine2VariableStep(
  polyLineToOffset: PolyLine2,
  edgeOffsetDistances: number[],
  collinearStartAndEndAddSegment = false,
): { pl: PolyLine2; edgeMapping: (number | undefined)[] } {
  const outputPl: number[] = [];

  const edgeCount = polyLine2EdgeCount(polyLineToOffset);
  if (edgeOffsetDistances.length !== edgeCount) {
    //TODO `buildRoofPerimeterBSP` is calling this function with edgeOffsetDistances.length !== edgeCount so keeping this commented out for now.
    //console.warn(`edgeOffsetDistances length ${edgeOffsetDistances.length} does not match edgeCount ${edgeCount}`);
  }

  const inputPlClosed = polyLine2IsClosed(polyLineToOffset);
  let endBackToStartEdgeTurning: boolean | undefined = undefined;

  // create an array of edgeIndices so that we can insert undefined values where transitions occur
  const edgeMapping: (number | undefined)[] = [];
  for (let i = 0; i < edgeCount; i++) {
    edgeMapping.push(i);
  }

  if (!inputPlClosed) {
    outputPl.push(
      ...offsetLine2Vertex(
        polyLineToOffset[0],
        polyLineToOffset[1],
        polyLineToOffset[2],
        polyLineToOffset[3],
        edgeOffsetDistances[0],
        true,
      ),
    );
  } else {
    const [prevEdgeX, prevEdgeY] = polyLine2EdgeStart(polyLineToOffset, edgeCount - 1);
    const [startEdgeX, startEdgeY] = polyLine2EdgeStart(polyLineToOffset, 0);
    const [endEdgeX, endEdgeY] = polyLine2EdgeEnd(polyLineToOffset, 0);
    endBackToStartEdgeTurning = vec2SequenceIsTurning(prevEdgeX, prevEdgeY, startEdgeX, startEdgeY, endEdgeX, endEdgeY);

    outputPl.push(
      ...polyLine2VariableMiterPt(
        prevEdgeX,
        prevEdgeY,
        startEdgeX,
        startEdgeY,
        endEdgeX,
        endEdgeY,
        endBackToStartEdgeTurning ? edgeOffsetDistances[edgeCount - 1] : edgeOffsetDistances[0],
        edgeOffsetDistances[0],
      ),
    );
  }

  let dataRemappingEdgeIndex = 0;
  for (let edge = 0; edge < edgeCount; edge++) {
    const nextEdgeIndex = (edge + 1) % edgeCount;
    const [nx, ny] = polyLine2EdgeNormal(polyLineToOffset, edge);
    const [pnx, pny] = edge > 0 ? polyLine2EdgeNormal(polyLineToOffset, edge - 1) : [0, 0];
    const [nnx, nny] =
      edge < edgeCount - 1 || inputPlClosed ? polyLine2EdgeNormal(polyLineToOffset, nextEdgeIndex) : [0, 0];
    const [edgeStartX, edgeStartY] = polyLine2EdgeStart(polyLineToOffset, edge);
    const [edgeEndX, edgeEndY] = polyLine2EdgeEnd(polyLineToOffset, edge);
    // First vert of edge (if needed)
    if (vec2DistSquared(nx, ny, pnx, pny) < DIST_EPSILON_SQUARED) {
      if (Math.abs(edgeOffsetDistances[edge] - edgeOffsetDistances[edge - 1]) > ZERO_EPSILON) {
        // Different offsets, need to add a new point. Otherwise, end point from previous segment is fine.
        outputPl.push(
          ...offsetLine2Vertex(edgeStartX, edgeStartY, edgeEndX, edgeEndY, edgeOffsetDistances[edge], true),
        );
        // add undefined to edgeIndices six segment is new and does not have a data association.
        edgeMapping.splice(dataRemappingEdgeIndex, 0, undefined);
        // increment index to account for additional segment for subsequent edges.
        dataRemappingEdgeIndex++;
      }
    }
    // Second vert of edge
    if (vec2DistSquared(nx, ny, nnx, nny) < DIST_EPSILON_SQUARED) {
      outputPl.push(...offsetLine2Vertex(edgeStartX, edgeStartY, edgeEndX, edgeEndY, edgeOffsetDistances[edge], false));
    } else {
      if (edge < edgeCount - 1 || inputPlClosed) {
        const [nextEdgeX, nextEdgeY] = polyLine2EdgeEnd(polyLineToOffset, nextEdgeIndex);

        outputPl.push(
          ...polyLine2VariableMiterPt(
            edgeStartX,
            edgeStartY,
            edgeEndX,
            edgeEndY,
            nextEdgeX,
            nextEdgeY,
            edgeOffsetDistances[edge],
            edgeOffsetDistances[nextEdgeIndex],
          ),
        );
      } else {
        outputPl.push(
          ...offsetLine2Vertex(edgeStartX, edgeStartY, edgeEndX, edgeEndY, edgeOffsetDistances[edge], false),
        );
      }
    }
    dataRemappingEdgeIndex++;
  }

  /**
   *  if first and last edges were collinear and closed in the input polyline and now have different offsets.
   *  And `collinearStartAndEndAddSegment` is true, add a segment to close the gap.
   */
  if (collinearStartAndEndAddSegment && inputPlClosed && !endBackToStartEdgeTurning) {
    const [endEdgeX, endEdgeY] = outputPl.slice(-2);
    const [startEdgeX, startEdgeY] = outputPl.slice(0, 2);
    // check if end and start are the same point
    if (vec2DistSquared(endEdgeX, endEdgeY, startEdgeX, startEdgeY) > DIST_EPSILON_SQUARED) {
      outputPl.push(startEdgeX, startEdgeY);
      edgeMapping.push(undefined);
    }
  }

  return { pl: polyLine2New(outputPl), edgeMapping };
}

/**
 * Offset a single polyLine2 edge, preserving adjacent edge angles
 */
export function offsetPolyLine2Edges(pl: PolyLine2, edgeIds: number[], dist: number, cfg?: SkeletonConfig): PolyLine2 {
  dist = -dist;
  const speeds = new Array<number>(polyLine2EdgeCount(pl)).fill(0.0);
  for (const edgeIdx of edgeIds) {
    speeds[edgeIdx] = 1;
  }

  if (dist < 0) {
    speeds.reverse();
  }

  const offset = offsetPolyLine2SSkFallback(pl, dist, speeds, cfg);

  if (polyLine2IsClosed(offset)) {
    polyLine2Shift(offset, 1);
  }

  return offset;
}

export function polyLine2VariableMiterPt(
  xx0: number,
  yy0: number,
  xx1: number,
  yy1: number,
  xx2: number,
  yy2: number,
  dist0: number,
  dist1: number,
) {
  if (abs(dist0 - dist1) < DIST_EPSILON) {
    return polyLine2MiterPt(xx0, yy0, xx1, yy1, xx2, yy2, dist0);
  }

  // line-line intersection of the offset lines

  let [normX0, normY0] =
    vec2DistSquared(xx0, yy0, xx1, yy1) > DIST_EPSILON_SQUARED
      ? line2Normal(xx0, yy0, xx1, yy1)
      : line2Normal(xx1, yy1, xx2, yy2);
  let [normX1, normY1] =
    vec2DistSquared(xx1, yy1, xx2, yy2) > DIST_EPSILON_SQUARED
      ? line2Normal(xx1, yy1, xx2, yy2)
      : line2Normal(xx0, yy0, xx1, yy1);

  const dot = vec2Dot(normX0, normY0, normX1, normY1);

  normX0 *= dist0;
  normY0 *= dist0;

  normX1 *= dist1;
  normY1 *= dist1;

  const x1 = xx0 + normX0;
  const y1 = yy0 + normY0;

  const x2 = xx1 + normX0;
  const y2 = yy1 + normY0;

  const x3 = xx1 + normX1;
  const y3 = yy1 + normY1;

  const x4 = xx2 + normX1;
  const y4 = yy2 + normY1;

  const ax = x1 - x2;
  const ay = y1 - y2;
  const bx = x3 - x4;
  const by = y3 - y4;

  const denom = ax * by - ay * bx;

  // parallel case
  if (abs(denom) < ZERO_EPSILON || 1 - abs(dot) < DIST_EPSILON) {
    vec2Result[0] = x2;
    vec2Result[1] = y2;

    return vec2Result;
  }

  // crossing intersection case
  const num0 = x1 * y2 - y1 * x2;
  const num1 = x3 * y4 - y3 * x4;
  const xnum = num0 * bx - ax * num1;
  const ynum = num0 * by - ay * num1;

  vec2Result[0] = xnum / denom;
  vec2Result[1] = ynum / denom;

  return vec2Result;
}

export function polyLine2MiterPt(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number, dist: number) {
  const [normX0, normY0] =
    vec2DistSquared(x0, y0, x1, y1) > DIST_EPSILON_SQUARED ? line2Normal(x0, y0, x1, y1) : line2Normal(x1, y1, x2, y2);
  const [normX1, normY1] =
    vec2DistSquared(x1, y1, x2, y2) > DIST_EPSILON_SQUARED ? line2Normal(x1, y1, x2, y2) : line2Normal(x0, y0, x1, y1);

  // add the normals to get the miter direction
  let [miterX, miterY] = vec2Add(normX0, normY0, normX1, normY1);

  // scale the miter direction
  const denom = miterX * normX0 + miterY * normY0;
  if (abs(denom) < ZERO_EPSILON) {
    vec2Result[0] = x1;
    vec2Result[1] = y1;
    return vec2Result;
  }
  const len = 1 / denom;
  [miterX, miterY] = vec2Scale(miterX, miterY, len);

  // add to the middle point and assign
  vec2Result[0] = miterX * dist + x1;
  vec2Result[1] = miterY * dist + y1;

  return vec2Result;
}

/**
 * This function offsets a 3D polyline using a plane as reference.
 *   1.- First we project the 3D polyline onto the plane.
 *   2.- Then we offset the 2D polyline.
 *   3.- After that, we convert the 2D polyline back to 3D.
 *   4.- Finally, we move the 3D polyline points back to their original distances to the plane.
 *
 * This could be useful for some trim generation, where we have a 3D path and a profile for
 * creating an extrusion, but first we want to offset it while keeping the height of each
 * point from the path (z component). In this case we can use this function using the XY plane
 * as the reference plane.
 */
export function offsetPolyLine3InPlane(plane: Plane, pl: PolyLine3, dist: number): PolyLine3 {
  const pl2 = planeProjectPolyLine3(plane, pl);

  const opl2 = offsetPolyLine2(pl2, dist);

  const pl3 = polyLine2OnPlane(plane, opl2);

  // move polyline points back to previous distances to the plane
  const [nX, nY, nZ] = planeNormal(plane);
  for (let i = 0, l = pl3.length; i < l; i += 3) {
    const dist = planeDistanceTo(plane, pl[i], pl[i + 1], pl[i + 2]);

    pl3[i] = pl3[i] + nX * dist;
    pl3[i + 1] = pl3[i + 1] + nY * dist;
    pl3[i + 2] = pl3[i + 2] + nZ * dist;
  }

  return pl3;
}

/**
 * This function offsets a 3D polyline using a plane as reference.
 *   1.- First we project the 3D polyline onto the plane.
 *   2.- Then we apply the variable offset to the 2D polyline.
 *   3.- After that, we convert the 2D polyline back to 3D.
 *   4.- Finally, we move the 3D polyline points back to their original distances to the plane.
 */
export function offsetPolyLine3VariableInPlane(plane: Plane, pl: PolyLine3, dists: number[]): PolyLine3 {
  const pl2 = planeProjectPolyLine3(plane, pl);

  const opl2 = offsetPolyLine2Variable(pl2, dists);

  const pl3 = polyLine2OnPlane(plane, opl2);

  // move polyline points back to previous distances to the plane
  const [nX, nY, nZ] = planeNormal(plane);
  for (let i = 0, l = pl3.length; i < l; i += 3) {
    const dist = planeDistanceTo(plane, pl[i], pl[i + 1], pl[i + 2]);

    pl3[i] = pl3[i] + nX * dist;
    pl3[i + 1] = pl3[i + 1] + nY * dist;
    pl3[i + 2] = pl3[i + 2] + nZ * dist;
  }

  return pl3;
}

export function offsetPolyLine3InXYPlane(pl: PolyLine3, dist: number): PolyLine3 {
  return offsetPolyLine3InPlane(planeNewXY(), pl, dist);
}

export function offsetPolyLine3VariableInXYPlane(pl: PolyLine3, dists: number[]): PolyLine3 {
  return offsetPolyLine3VariableInPlane(planeNewXY(), pl, dists);
}

export function offsetLine2Vertex(x0: number, y0: number, x1: number, y1: number, dist: number, start: boolean) {
  const [normX, normY] = line2Normal(x0, y0, x1, y1);

  if (start) {
    vec2Result[0] = normX * dist + x0;
    vec2Result[1] = normY * dist + y0;
  } else {
    vec2Result[0] = normX * dist + x1;
    vec2Result[1] = normY * dist + y1;
  }

  return vec2Result;
}

/**
 * Offsets a single edge of a PolyLine2 until it is tangent to a point.
 */
export function offsetPolyLine2EdgeToPoint(
  pl: PolyLine2,
  edgeId: number,
  x: number,
  y: number,
  angleTolerance: number = ANGLE_EPSILON,
): PolyLine2 {
  const [cx, cy] = closestPointPolyLine2Edge(pl, edgeId, x, y, false);
  const [dx, dy] = line2Vec([cx, cy, x, y]); // normalized

  const [ex, ey] = polyLine2EdgeNormal(pl, edgeId);
  const angle = angleClean(vec2AngleBetween(ex, ey, dx, dy));
  let dist = vec2Len(x - cx, y - cy);
  if (Math.abs(angle) < angleTolerance) {
    dist = -dist;
  }
  return offsetPolyLine2Edges(polyLine2Copy(pl), [edgeId], dist);
}

/**
 * Offsets a polyline and closes it again
 */
export function polyLine2Thicken(e: PolyLine2, dist: number, centered = false): PolyLine2 {
  if (centered) {
    const offset = offsetPolyLine2(e, dist / 2);
    e = polyLine2ReverseCopy(e);
    return polyLine2Concat(offsetPolyLine2(e, dist / 2), offset, true);
  }

  const offset = offsetPolyLine2(e, dist);
  e = polyLine2ReverseCopy(e);

  return polyLine2Concat(e, offset, true);
}

/**
 * Offsets the given edge of the polyline and splices the thickened edge into the source polyline,
 * yielding a new PolyLine2 with 2 additional vertices
 */
export function polyLine2ThickenEdge(pl: PolyLine2, edgeIndex: number, dist: number): PolyLine2 {
  edgeIndex = polyLine2RepairEdgeIndex(pl, edgeIndex);
  const edge = polyLine2Edge(pl, edgeIndex);
  const edgeOffset = offsetPolyLine2(edge, dist);

  const plNew = polyLine2New(pl.length + 4);

  const edgeIndex2 = edgeIndex * 2;

  plNew.set(pl, 0);
  plNew.set(edgeOffset, edgeIndex2 + 2);
  plNew.set(pl.slice(edgeIndex2 + 2), edgeIndex2 + 6);

  return plNew;
}

/**
 * Offsets the given edge of the polyline and closes it again, returning a rectangle
 */
export function polyLine2ThickenedEdge(pl: PolyLine2, edgeIndex: number, dist: number): PolyLine2 {
  return polyLine2Thicken(polyLine2Edge(pl, edgeIndex), dist);
}

export function* polyLine2ConservativeRasterFill(pl: PolyLine2): Generator<Vec2> {
  // Enlarge the polygon by 1/sqrt(2) to the direction of each edge's normal, to make it suitable for conservative rasterization.
  // Note that this is only reliable for convex polylines!
  // TODO: We could make the geometry a bit smaller by computing the Minkowski sum of the polyline and an axis aligned unit square,
  // but this approach is much simpler.
  // https://developer.nvidia.com/content/dont-be-conservative-conservative-rasterization
  const conservativePl = offsetPolyLine2(pl, Math.SQRT1_2);
  yield* polyLine2RasterFill(conservativePl);
}

/**
 * Offset a PolyLine2 using a straight skeleton, but fallback to naive offset if straight skeleton fails.
 * @TODO This should be the default offsetPolyLine2 implementation. Andrew to update.
 */
export function offsetPolyLine2SSkFallback(
  pl: PolyLine2,
  offset: number,
  speeds?: EdgeSpeeds,
  cfg: SkeletonConfig = DEFAULT_CONFIG,
): PolyLine2 {
  const { success, pls } = straightSkeletonOffset(pl, offset, speeds, cfg);
  console.assert(success, "Straight skeleton offset failed, falling back to normal offset");
  if (!success) {
    return offsetPolyLine2(pl, offset);
  }
  return pls[0];
}

/**
 * clone of polyLine2Thicken using the straight skeleton
 */
export function polyLine2ThickenSSk(e: PolyLine2, dist: number, centered = false): PolyLine2 {
  if (centered) {
    const offset = offsetPolyLine2SSkFallback(e, dist / 2);
    e = polyLine2ReverseCopy(e);
    return polyLine2Concat(offsetPolyLine2SSkFallback(e, dist / 2), offset, true);
  }

  const offset = offsetPolyLine2SSkFallback(e, dist);
  e = polyLine2ReverseCopy(e);

  return polyLine2Concat(e, offset, true);
}
